<template>
   <div class="declaration-readonly">
      <section class="statement-panel">
         <div class="statement-header">
            <div>
               <div class="section-eyebrow">Applicant Statement</div>
               <h3>Declaration of Membership</h3>
            </div>
            <v-chip small color="primary" outlined>{{ typeLabel(membership ? membership.membertype : '') }}</v-chip>
         </div>
         <div class="statement-body">
            <v-avatar size="56" class="statement-avatar">
               <v-img :src="memberPhoto(membership)"></v-img>
            </v-avatar>
            <div>
               <p>
                  I, <i>{{ membership ? membership.fullname : "" }}</i>, desire to be admitted as
                  <span v-html="membership ? formatCategory(membership.membertype) : ''"></span>
                  of the Malaysian Institute of Planners and declare that the information supplied herewith is true in every respect.
                  I undertake to adhere to the Malaysian Institute of Planners Code of Professional Conduct, Regulations, and Charter
                  &amp; Bylaws throughout my membership of the Malaysian Institute of Planners.
               </p>
               <p class="muted">
                  The Malaysian Institute of Planners Code of Professional Conduct, Regulations and Charter &amp; Bylaws are subject to change
                  and are available for members to view online or a hard copy is available on request.
               </p>
            </div>
         </div>
      </section>

      <section class="declaration-section">
         <div class="section-header">
            <div>
               <div class="section-eyebrow">Endorsement Records</div>
               <h3>Proposer and Seconder History</h3>
            </div>
            <v-chip small :color="overallColor" dark>{{ overallLabel }}</v-chip>
         </div>

         <v-alert v-if="message" dense text :type="messageType" class="mb-4">
            {{ message }}
         </v-alert>

         <div v-if="loading" class="loading-row">
            <v-progress-circular indeterminate size="22" width="3" color="primary" class="mr-3"></v-progress-circular>
            Loading declaration records...
         </div>

         <div v-else-if="visibleSections.length === 0" class="empty-state">
            <v-icon size="42" color="blue-grey lighten-1">mdi-file-search-outline</v-icon>
            <div>No declaration or endorsement record found.</div>
         </div>

         <div v-else class="section-list">
            <div v-for="section in visibleSections" :key="section.key" class="record-block">
               <div class="record-block-header">
                  <div>
                     <h4>{{ section.title }}</h4>
                     <p>{{ section.summary }}</p>
                  </div>
                  <v-chip small :color="sectionStatusColor(section)" dark>{{ sectionStatusLabel(section) }}</v-chip>
               </div>

               <div v-if="section.people && section.people.length" class="person-grid">
                  <div v-for="person in section.people" :key="section.key + '-' + person.role" class="person-panel" :class="'person-panel-' + responseClass(person.status)">
                     <div class="person-top">
                        <v-avatar size="58" class="person-avatar mr-3">
                           <v-img :src="personPhoto(person)"></v-img>
                        </v-avatar>
                        <div class="person-main">
                           <div class="role-label">{{ person.role }}</div>
                           <strong>{{ person.fullname || '-' }}</strong>
                           <span>{{ typeLabel(person.membertype) }}</span>
                        </div>
                        <div class="person-actions">
                           <v-chip x-small :color="responseColor(person.status)" dark>{{ responseLabel(person.status) }}</v-chip>
                           <v-btn
                              v-if="canResetEndorsement(section, person)"
                              x-small
                              outlined
                              color="error"
                              :loading="resettingKey === resetKey(section, person)"
                              @click="resetEndorsement(section, person)"
                           >
                              Reset {{ person.role }}
                           </v-btn>
                        </div>
                     </div>

                     <div class="detail-grid">
                        <div>
                           <span>Member ID</span>
                           <strong>{{ person.memberid || '-' }}</strong>
                        </div>
                        <div>
                           <span>LPBM No.</span>
                           <strong>{{ person.lpbmno || '-' }}</strong>
                        </div>
                        <div>
                           <span>Email</span>
                           <strong>{{ person.contactemail || '-' }}</strong>
                        </div>
                        <div>
                           <span>Mobile</span>
                           <strong>{{ person.contactmobile || '-' }}</strong>
                        </div>
                        <div>
                           <span>Sent</span>
                           <strong>{{ formatDateTime(person.sent_at) }}</strong>
                        </div>
                        <div>
                           <span>Responded</span>
                           <strong>{{ formatDateTime(person.responded_at) }}</strong>
                        </div>
                     </div>
                  </div>
               </div>

               <div v-else class="empty-inline">
                  No proposer or seconder has been recorded for this section.
               </div>
            </div>
         </div>
      </section>
   </div>
</template>

<script>
const axios = require('axios')

export default {
   name: "TableDeclReadOnly",

   props: {
      adminMode: {
         type: Boolean,
         default: false,
      },
   },

   data() { return {
      userid: 0,
      membership: null,
      loading: false,
      message: '',
      messageType: 'info',
      sections: [],
      resettingKey: '',
      membertypes: [
         { const: "ASC", title: "an <b>Associate Member</b>", label: "Associate Member" },
         { const: "STU", title: "a <b>Student Member</b>", label: "Student Member" },
         { const: "GRA", title: "a <b>Graduate Member</b>", label: "Graduate Member" },
         { const: "COR", title: "a <b>Corporate Member</b>", label: "Corporate Member" },
         { const: "FEL", title: "a <b>Fellow Member</b>", label: "Fellow Member" },
         { const: "HON", title: "a <b>Honorary Member</b>", label: "Honorary Member" },
         { const: "PAT", title: "a <b>Patron</b>", label: "Patron" }
      ],
   }},

   computed: {
      visibleSections() {
         return (this.sections || []).filter((section) => {
            return section && (
               (section.people && section.people.length)
               || section.key === 'application'
               || (['corporate_upgrade', 'graduate_upgrade'].includes(section.key) && parseInt(section.status || 0) > 0)
            )
         })
      },

      overallLabel() {
         const people = this.visibleSections.flatMap(section => section.people || [])
         if (!people.length) {
            return 'No Record'
         }
         if (people.every(person => parseInt(person.status) === 1)) {
            return 'Completed'
         }
         if (people.some(person => parseInt(person.status) === 2)) {
            return 'Action Required'
         }
         return 'Pending'
      },

      overallColor() {
         if (this.overallLabel === 'Completed') {
            return 'green darken-1'
         }
         if (this.overallLabel === 'Action Required') {
            return 'red darken-1'
         }
         if (this.overallLabel === 'Pending') {
            return 'orange darken-2'
         }
         return 'blue-grey'
      }
   },

   methods: {
      setuserid(pUserID) {
         if (parseInt(this.userid || 0) !== parseInt(pUserID || 0)) {
            this.membership = null
            this.sections = []
            this.message = ''
            this.resettingKey = ''
         }
         this.userid = pUserID
         this.initialize()
      },

      initialize(keepMessage) {
         if (!this.userid) {
            return
         }

         this.loading = true
         if (!keepMessage) {
            this.message = ''
         }
         axios.get('/member/decl/summary/' + this.userid)
         .then((resp) => {
            if (resp.data && resp.data.status === 'OK') {
               this.membership = resp.data.data.member
               this.sections = resp.data.data.sections || []
            } else {
               this.messageType = 'error'
               this.message = resp.data && resp.data.error ? resp.data.error : 'Unable to load declaration records.'
               this.sections = []
            }
         })
         .catch(() => {
            this.messageType = 'error'
            this.message = 'Unable to load declaration records.'
            this.sections = []
         })
         .finally(() => {
            this.loading = false
         })
      },

      formatCategory(e) {
         var membercategory = this.membertypes.find(obj => obj.const === e)
         return membercategory ? membercategory.title : ''
      },

      typeLabel(e) {
         var membercategory = this.membertypes.find(obj => obj.const === e)
         return membercategory ? membercategory.label : (e || '-')
      },

      personPhoto(person) {
         if (person && person.userid) {
            return '/member/photo/' + person.userid + '?v=' + Date.now()
         }
         if (person && person.photolink) {
            return '/' + String(person.photolink).replace(/^\/+/, '')
         }
         return '/images/miplogo-300x300.png'
      },

      memberPhoto(member) {
         if (member && member.userid) {
            return '/member/photo/' + member.userid + '?v=' + Date.now()
         }
         if (member && member.photolink) {
            return '/' + String(member.photolink).replace(/^\/+/, '')
         }
         return '/images/miplogo-300x300.png'
      },

      responseLabel(value) {
         const status = parseInt(value)
         if (status === 1) {
            return 'Accepted'
         }
         if (status === 2) {
            return 'Rejected'
         }
         return 'Pending'
      },

      responseColor(value) {
         const status = parseInt(value)
         if (status === 1) {
            return 'green darken-1'
         }
         if (status === 2) {
            return 'red darken-1'
         }
         return 'orange darken-2'
      },

      responseClass(value) {
         const status = parseInt(value)
         if (status === 1) return 'accepted'
         if (status === 2) return 'rejected'
         return 'pending'
      },

      sectionStatusLabel(section) {
         if (section.key === 'application' && ['Completed', 'Rejected', 'Pending', 'No record'].includes(section.status)) {
            return section.status === 'No record' ? 'No Record' : section.status
         }

         if (['corporate_upgrade', 'graduate_upgrade'].includes(section.key) && parseInt(section.status || 0) === 4) {
            return 'Need Resubmission'
         }

         const people = section.people || []
         if (!people.length) {
            return 'No Record'
         }
         if (people.every(person => parseInt(person.status) === 1)) {
            return 'Completed'
         }
         if (people.some(person => parseInt(person.status) === 2)) {
            return 'Rejected'
         }
         return 'Pending'
      },

      sectionStatusColor(section) {
         const label = this.sectionStatusLabel(section)
         if (label === 'Completed') {
            return 'green darken-1'
         }
         if (label === 'Rejected') {
            return 'red darken-1'
         }
         if (label === 'Pending') {
            return 'orange darken-2'
         }
         if (label === 'Need Resubmission') {
            return 'deep-purple'
         }
         return 'blue-grey'
      },

      canResetEndorsement(section, person) {
         if (!this.adminMode || !section || !person || !['Proposer', 'Seconder'].includes(person.role)) {
            return false
         }

         if (section.key === 'application') {
            return this.membership
               && [0, 1].includes(parseInt(this.membership.memberstatus))
               && (parseInt(person.status || 0) !== 0 || parseInt(this.membership.memberstatus) === 1)
         }

         return ['corporate_upgrade', 'graduate_upgrade'].includes(section.key)
            && parseInt(person.status || 0) !== 0
      },

      resetKey(section, person) {
         return section.key + '-' + String(person.role || '').toLowerCase()
      },

      resetEndorsement(section, person) {
         if (!this.membership || !this.membership.userid) {
            return
         }

         const role = String(person.role || '').toLowerCase()
         const isApplication = section.key === 'application'
         const confirmation = isApplication
            ? 'Reset this membership application nomination? The application will return to Applying so the member can select a new ' + role + '.'
            : 'Reset this upgrade endorsement? The member will be asked to select a new proposer and seconder.'
         if (!confirm(confirmation)) {
            return
         }

         this.resettingKey = this.resetKey(section, person)
         this.message = ''

         const endpoint = isApplication
            ? '/admin/decl/resetdecl' + role
            : '/admin/decl/resetupgradeendorsement'
         const payload = isApplication
            ? { userid: this.membership.userid }
            : { userid: this.membership.userid, section: section.key, role: role }

         axios.post(endpoint, payload)
         .then((resp) => {
            if (resp.data && resp.data.status === 'OK') {
               this.messageType = 'success'
               this.message = resp.data.message || 'Endorsement reset successfully.'
               this.initialize(true)
               return
            }

            this.messageType = 'error'
            this.message = resp.data && resp.data.error ? resp.data.error : 'Unable to reset endorsement.'
         })
         .catch(() => {
            this.messageType = 'error'
            this.message = 'Unable to reset endorsement.'
         })
         .finally(() => {
            this.resettingKey = ''
         })
      },

      formatDateTime(value) {
         if (!value) {
            return '-'
         }
         return this.$moment(value).format('DD/MM/YYYY h:mm A')
      },
   },

   mounted: function () {
      this.$emit('DeclMounted')
   }
}
</script>

<style scoped>
.declaration-readonly {
   background: linear-gradient(180deg, #eef4fb 0%, #f8fafc 42%, #f8fafc 100%);
   min-height: calc(100vh - 112px);
   padding: 18px;
}

.statement-panel,
.declaration-section {
   background: #ffffff;
   border: 1px solid #dce6f2;
   border-radius: 8px;
   box-shadow: 0 14px 32px rgba(15, 23, 42, .06);
   margin-bottom: 16px;
   overflow: hidden;
   padding: 0;
}

.statement-panel {
   border-top: 4px solid #0f766e;
}

.statement-header,
.section-header {
   align-items: flex-start;
   background: #ffffff;
   border-bottom: 1px solid #edf1f7;
   display: flex;
   gap: 16px;
   justify-content: space-between;
   padding: 18px 20px 14px;
}

.statement-body {
   align-items: flex-start;
   display: flex;
   gap: 16px;
   padding: 18px 20px 20px;
}

.statement-avatar,
.person-avatar {
   border: 2px solid #e0f2fe;
   box-shadow: 0 8px 18px rgba(15, 23, 42, .12);
}

.section-eyebrow,
.role-label,
.detail-grid span {
   color: #64748b;
   font-size: 11px;
   font-weight: 800;
   letter-spacing: 0;
   text-transform: uppercase;
}

h3,
h4 {
   color: #0f172a;
   letter-spacing: 0;
   margin: 0;
}

.statement-panel h3,
.declaration-section h3 {
   font-size: 20px;
   margin-top: 3px;
}

.statement-panel p {
   color: #334155;
   line-height: 1.65;
   margin: 0 0 12px;
}

.muted {
   color: #64748b !important;
   margin-bottom: 0 !important;
}

.record-block-header,
.person-top {
   align-items: flex-start;
   display: flex;
   gap: 16px;
   justify-content: space-between;
}

.section-list {
   display: grid;
   gap: 14px;
   padding: 16px;
}

.record-block {
   border: 1px solid #dce6f2;
   border-radius: 8px;
   overflow: hidden;
}

.record-block-header {
   background: linear-gradient(90deg, #f8fbff 0%, #eff6ff 100%);
   border-bottom: 1px solid #e2e8f0;
   padding: 14px 16px;
}

.record-block-header h4 {
   font-size: 16px;
}

.record-block-header p {
   color: #64748b;
   margin: 4px 0 0;
}

.person-grid {
   display: grid;
   gap: 12px;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   padding: 14px;
}

.person-panel {
   background: linear-gradient(180deg, #ffffff 0%, #fbfdff 100%);
   border: 1px solid #e5eef8;
   border-radius: 8px;
   box-shadow: 0 10px 24px rgba(15, 23, 42, .05);
   padding: 14px;
   position: relative;
}

.person-panel::before {
   border-radius: 8px 0 0 8px;
   content: "";
   height: 100%;
   left: 0;
   position: absolute;
   top: 0;
   width: 4px;
}

.person-panel-accepted::before {
   background: #16a34a;
}

.person-panel-pending::before {
   background: #f97316;
}

.person-panel-rejected::before {
   background: #dc2626;
}

.person-main {
   flex: 1;
   min-width: 0;
}

.person-main strong,
.detail-grid strong {
   color: #0f172a;
   display: block;
   overflow-wrap: anywhere;
}

.person-main span {
   color: #64748b;
   display: block;
   font-size: 12px;
   margin-top: 2px;
}

.person-actions {
   align-items: flex-end;
   display: flex;
   flex-direction: column;
   gap: 8px;
}

.detail-grid {
   display: grid;
   gap: 12px;
   grid-template-columns: repeat(2, minmax(0, 1fr));
   margin-top: 14px;
}

.detail-grid div {
   background: #f8fafc;
   border: 1px solid #eef2f7;
   border-radius: 6px;
   min-width: 0;
   padding: 10px;
}

.detail-grid strong {
   font-size: 13px;
   margin-top: 3px;
}

.loading-row,
.empty-state,
.empty-inline {
   align-items: center;
   color: #64748b;
   display: flex;
   justify-content: center;
   min-height: 96px;
   text-align: center;
}

.empty-state {
   flex-direction: column;
   gap: 10px;
}

.empty-inline {
   min-height: 72px;
}

@media (max-width: 960px) {
   .person-grid,
   .detail-grid {
      grid-template-columns: 1fr;
   }

   .statement-header,
   .statement-body,
   .section-header,
   .record-block-header {
      display: block;
   }

   .section-header .v-chip,
   .record-block-header .v-chip {
      margin-top: 10px;
   }
}
</style>
